// Mermaid Diagram Block
import mermaid from 'mermaid';

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose',
  fontFamily: 'inherit'
});

class MermaidBlock {
  static get toolbox() {
    return {
      title: 'Diagram',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.data = {
      code: data.code || 'graph TD\n    A[Start] --> B{Decision}\n    B -->|Yes| C[Action 1]\n    B -->|No| D[Action 2]',
      caption: data.caption || ''
    };
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    this.wrapper = null;
    this.renderTimeout = null;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('mermaid-block');
    this._renderMermaidBlock();
    return this.wrapper;
  }

  async _renderDiagram(container) {
    try {
      // Generate unique ID
      const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Clear previous content
      container.innerHTML = '';
      
      // Create a temporary element for mermaid to render into
      const tempDiv = document.createElement('div');
      tempDiv.id = id;
      container.appendChild(tempDiv);

      // Render the diagram
      const { svg } = await mermaid.render(id, this.data.code);
      container.innerHTML = svg;
      
      // Style the SVG
      const svgElement = container.querySelector('svg');
      if (svgElement) {
        svgElement.style.maxWidth = '100%';
        svgElement.style.height = 'auto';
      }
    } catch (error) {
      container.innerHTML = `
        <div class="text-red-500 dark:text-red-400 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded">
          <strong>Diagram Error:</strong> ${error.message || 'Invalid Mermaid syntax'}
        </div>
      `;
    }
  }

  _renderMermaidBlock() {
    this.wrapper.innerHTML = `
      <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="mermaid-block">
        <div class="flex items-center justify-between px-4 py-2 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
            Mermaid Diagram
          </span>
          ${!this.readOnly ? `
            <a href="https://mermaid.js.org/syntax/flowchart.html" target="_blank" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">
              Syntax Reference
            </a>
          ` : ''}
        </div>
        
        ${!this.readOnly ? `
          <div class="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
            <div class="p-4 bg-slate-50 dark:bg-slate-900">
              <label class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block">Code</label>
              <textarea 
                class="mermaid-input w-full min-h-[200px] p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded font-mono text-sm text-slate-700 dark:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Mermaid diagram code..."
                spellcheck="false"
              >${this.data.code}</textarea>
            </div>
            <div class="p-4 bg-white dark:bg-slate-950">
              <label class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block">Preview</label>
              <div class="mermaid-preview min-h-[200px] flex items-center justify-center"></div>
            </div>
          </div>
        ` : `
          <div class="p-6 bg-white dark:bg-slate-950">
            <div class="mermaid-preview flex items-center justify-center"></div>
          </div>
        `}
        
        ${this.data.caption || !this.readOnly ? `
          <div class="px-4 py-2 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
            ${!this.readOnly ? `
              <input 
                type="text"
                class="caption-input w-full text-sm text-slate-600 dark:text-slate-400 bg-transparent border-0 focus:outline-none"
                placeholder="Add a caption..."
                value="${this.data.caption}"
              />
            ` : this.data.caption ? `
              <p class="text-sm text-slate-600 dark:text-slate-400 text-center">${this.data.caption}</p>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;

    // Render initial diagram
    const previewContainer = this.wrapper.querySelector('.mermaid-preview');
    if (previewContainer) {
      this._renderDiagram(previewContainer);
    }

    // Event listeners
    if (!this.readOnly) {
      const codeInput = this.wrapper.querySelector('.mermaid-input');
      const captionInput = this.wrapper.querySelector('.caption-input');

      if (codeInput) {
        codeInput.addEventListener('input', (e) => {
          this.data.code = e.target.value;
          
          // Debounce diagram rendering
          if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
          }
          this.renderTimeout = setTimeout(() => {
            this._renderDiagram(previewContainer);
          }, 500);
        });
      }

      if (captionInput) {
        captionInput.addEventListener('input', (e) => {
          this.data.caption = e.target.value;
        });
      }
    }
  }

  save() {
    return {
      code: this.data.code,
      caption: this.data.caption
    };
  }

  static get sanitize() {
    return {
      code: true,
      caption: false
    };
  }
}

export default MermaidBlock;
